/**
 * 「**还有效**（还没到期、但已经不是今天布置的）」作业项的**共享判据与文案**。
 *
 * ## 这个文件为什么存在（用户原话）
 *
 * > 昨天是周五，录了一批次截止日期为周日的作业。小孩当时有两项作业还没有完成。
 * > 昨天没有完成，今天来看的时候，他点开来看想点"完成"，就发现**这两项作业的记录都没有了**
 * > —— 不管是**当天的作业**还是**超时**的作业都没有。
 *
 * 根因是两个视图各用了一个**不同的字段**，而这两个字段自"截止日期"上线后**不再相等**：
 *
 * | 视图 | 判据（改造前两条判据其实是同一个日子，所以没有缝） |
 * | --- | --- |
 * | 「今天的作业」 | `checkin.due_date = 今天` —— `due_date` 是**布置日** |
 * | 「超时」 | `checkin.due_at < now()` 且状态未终结 —— `due_at` 是**截止时刻** |
 * | **（缺的那一个）** | `due_date < 今天` **且** `due_at >= now()` **且**未终结 |
 *
 * 改造前 `due_at` 只由「布置日 + `due_time`」推导，于是"昨天没做完 ⇒ 今天必然已超时"，
 * 缝隙不可能存在。2026-10-16 上线"截止日期（`node.due_date`）"之后，
 * 周五布置、周日截止的作业在周六**既不是今天的作业**（布置日是周五）、
 * **也还没超时**（`due_at` 是周日 23:59）—— 它掉进了两个视图之间的缝里，
 * 孩子看不到、也就点不了「完成」。这个文件补的就是那一格。
 *
 * ## 判据只有这一份
 *
 * 服务端两条队列（`apps/api/src/modules/checkins/checkins.service.ts` 的
 * `listOverdueCheckins` / `listOpenCheckins`）分别用 SQL 表达同一件事，
 * 而"哪些行属于「还有效」"的**权威定义**是本文件的 `isHomeworkOpenQueueItem()`：
 * SQL 只是它在数据库侧的镜像（分页/总数必须在库里算，见那个函数的注释）。
 * 两边任何一处改动都必须让 `packages/shared/src/__tests__/homework-open.test.ts`
 * 与 `apps/api/src/__tests__/homework.open.test.ts` 同时通过。
 *
 * ## 三条不能破的既有约定
 *
 * 1. **「超时」是派生状态、跨天保留**，本文件不改它：超时仍然由
 *    `isOverdueCheckin()`（`apps/api/src/modules/checkins/checkins.rules.ts`）判，
 *    「还有效」与它**严格互补**（`due_at < now` 与 `due_at >= now`），
 *    所以同一条作业绝不会同时出现在两个队列里。
 * 2. **`retained`（已移除但有记录）永不物理删除**，而且它在队列里的可见性
 *    与"今日清单/超时队列"**同一条口径**：`habit.active = false` 时，
 *    只有"已经有动静"（`status <> 'pending'`）的行才还看得见 —— 见下面 `habitActive` 参数。
 * 3. **`dueTimeSource === 'dayEnd'` 时绝不显示 23:59**：队列里的每一条都是
 *    "还没到期"的，所以徽章只说**哪一天**截止（`describeHomeworkOpenBadge()`），
 *    不把 `due_at` 的时刻当人话写出来 —— 那个 23:59 是"当天结束前"的默认值，
 *    不是家长填的截止时刻。
 */

import type { CheckinStatus } from './constants.js';
import { WEEKDAY_LABELS } from './homework-due.js';
import { addDays, weekdayOf } from './time.js';

// ================================================================ 状态集合

/**
 * 「未完成类」状态 —— **全项目唯一的一份**。
 *
 * 它是"超时"与"还有效"两个派生队列共同的状态白名单：
 * `pending / running / paused / auto_paused / submitted`。
 *
 * 入选理由（改动前写在 `apps/api/src/modules/checkins/checkins.rules.ts`，
 * 2026-10-17 搬到这里，因为作业页现在也要用它）：
 *  - 不含 `done` / `skipped`：已经有结论了；
 *  - 不含 `missed`：那是 worker 结算出来的终态，不是"超时"（它已经转成"没做"，
 *    而作业项是一次性任务，worker **永不**把它结算成 `missed`，
 *    见 `apps/worker/src/jobs.ts` 的 `selectSettleable()`）；
 *  - **含 `submitted`**：作业交了但家长还没验收 —— 对家长来说这正是待办，
 *    而它同时意味着"孩子已经交了、只是卡在验收"，绝不能算成孩子超时。
 *    作业页面据 `status` 区分这两种含义即可。
 */
export const UNFINISHED_STATUSES: readonly CheckinStatus[] = [
  'pending',
  'running',
  'paused',
  'auto_paused',
  'submitted',
];

/** 「已经有结论」的状态：这些行永远不进任何"待做"队列。 */
export const FINISHED_STATUSES: readonly CheckinStatus[] = ['done', 'skipped', 'missed'];

// ================================================================ 「还有效」队列

/** `isHomeworkOpenQueueItem()` 的入参（字段名与服务端行一一对应）。 */
export interface HomeworkOpenQueueInput {
  /** `checkin.status`。 */
  status: CheckinStatus;
  /** **布置日**（`checkin.due_date`，'YYYY-MM-DD'）——「这是哪一天的作业」。 */
  planDate: string;
  /** **截止时刻**（`checkin.due_at`）= 「截止日 + 科目截止时刻」换算成 UTC。 */
  dueAt: Date;
  /** 家庭时区下的"今天"（'YYYY-MM-DD'）。 */
  today: string;
  /** 服务端"现在"（判定"还没到期"用；可注入，便于测试）。 */
  now: Date;
  /**
   * `habit.active`：false = 这一项已被移出计划、只因有打卡记录而保留（`retained`）。
   *
   * ⚠️ 与今日清单/超时队列**同一条口径**：停用 + 从未动过（`pending`）的行不显示，
   * 但"已经有动静"（完成/提交/计时中/放弃）的行是孩子的事实记录，必须继续看得见。
   * 缺省 `true`（作业项默认都在计划里）。
   */
  habitActive?: boolean;
}

/**
 * 一条作业项**属不属于「还有效」队列**（纯函数，本模块的核心）。
 *
 * 判据（四条，**顺序无关但缺一不可**）：
 *
 *  1. **状态未终结**（`UNFINISHED_STATUSES`）—— `done` / `skipped` / `missed` 不欠任何东西；
 *  2. **还没到期**：`dueAt >= now`（严格互补于「超时」的 `dueAt < now`，所以两个队列
 *     不可能同时收下同一行；"恰好到点"按未到期处理，与 `isOverdueCheckin()` 的
 *     严格 `<` 保持同一刻度）；
 *  3. **不是今天布置的**：`planDate < today` —— `planDate === today` 的项归「今天的作业」，
 *     `planDate > today` 的项是**提前录入**的（家长为明天/后天的作业预先录入），
 *     今天不该催孩子做，也刻意不在这里出现（它们到了那一天会进「今天的作业」）；
 *  4. **停用保留项**：`habitActive === false` 时，只有 `status <> 'pending'` 才可见
 *     （与今日清单、超时队列逐字相同的规则，见 `HomeworkOpenQueueInput.habitActive`）。
 *
 * ⚠️ 为什么不用「`!isOverdueCheckin(...)`」来表达第 2 条：
 * 那个函数对**终态**返回 false（"不超时"），于是"已完成的项"会被算成"还有效"。
 * 两条判据的**定义域不同**（一个问"超时吗"，一个问"还要做吗"），
 * 所以这里把四条都写出来，而不是拿一个否定去凑。
 */
export function isHomeworkOpenQueueItem(input: HomeworkOpenQueueInput): boolean {
  if (!UNFINISHED_STATUSES.includes(input.status)) return false;
  const habitActive = input.habitActive ?? true;
  if (!habitActive && input.status === 'pending') return false;
  if (input.dueAt.getTime() < input.now.getTime()) return false;
  return input.planDate < input.today;
}

/**
 * 「还有效」队列的**排序键**：按截止时刻**由近到远**（越早到期越靠前）。
 *
 * 服务端的 `ORDER BY due_at ASC` 是它的 SQL 镜像；两边都写成"升序"，
 * 因为这一份是"还来得及做"的清单 —— 先做最先到期的。
 * 与「超时」的 `ORDER BY due_at DESC`（最新截止的最紧急）**方向相反**是刻意的：
 * 那边是欠账，越新欠的越要注意；这边是计划，越早到期的越要先做。
 *
 * 同一时刻的两条按名称稳定排序，保证"同一份数据两次请求顺序一致"。
 */
export function compareHomeworkOpenQueueEntries(
  a: { dueAt: string; habitName?: string },
  b: { dueAt: string; habitName?: string },
): number {
  if (a.dueAt !== b.dueAt) return a.dueAt < b.dueAt ? -1 : 1;
  return (a.habitName ?? '').localeCompare(b.habitName ?? '');
}

/**
 * 同一条打卡在同一份列表里**只留一行**（去重，保持首次出现的顺序）。
 *
 * 为什么需要：一个孩子可能有多项作业（用户报的那次就是 2 项），而
 * `uq_checkin_slot(habit_id, due_date)` 只保证"同一 habit 同一天一行"——
 * 若将来出现同一天两张作业单、或同名科目被复用，同一条 `checkin` 仍可能被
 * 两次查询拼进同一个数组（例如页面把"还有效"与"今天的作业"的前缀缓存合并渲染）。
 * 去重按 `id`（= checkinId）而不是按名字：同名作业项可以是**两项**真实作业，
 * 按名字去重会把孩子真要做的一项吞掉。
 */
export function dedupeHomeworkQueueEntries<T extends { id: string }>(entries: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const entry of entries) {
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry);
  }
  return out;
}

// ================================================================ 徽章文案

/** `describeHomeworkOpenBadge()` 的入参。 */
export interface HomeworkOpenBadgeInput {
  /** **布置日**（`checkin.due_date`，'YYYY-MM-DD'）。 */
  planDate: string;
  /** **截止日**（`node.due_date ?? checkin.due_date`，'YYYY-MM-DD'）。 */
  deadlineDate?: string | null;
  /** 家庭时区下的"今天"（'YYYY-MM-DD'）—— 决定要不要说"今天/明天"。 */
  today: string;
}

/**
 * 「还有效」项的日期徽章 —— **逐字**长这样：
 *
 * ```
 * 📅 周五 10-16 布置 · 周日 10-18 截止
 * 📅 周五 10-16 布置 · 今天截止        ← 截止日就是今天（昨天布置、留到今天）
 * 📅 周五 10-16 布置 · 明天截止
 * 📅 周五 10-16 布置 · 当天截止        ← 理论上不会出现（"当天"就必然已过期）
 * ```
 *
 * 三条刻意的取舍：
 *
 * 1. **一定说出"哪天布置的"**。它**不是**今天的作业，把孩子看到的第一句话
 *    说成"今天的作业"就是误导 —— 这也正是任务里点名的一条。
 * 2. **一定说出"哪天截止的"**，否则孩子不知道还来不来得及。
 * 3. ⛔ **绝不写 `23:59`**。队列里的截止时刻大多来自 `dueTimeSource === 'dayEnd'`
 *    的默认值（语义是"那一天结束前"），把 23:59 当成人话写出来会被读成
 *    "家长要求我 23:59 交"，与既有约定冲突（见 `homework-due.ts` 的文案规则）。
 *    所以这里只给**日期**，不给时刻。
 */
export function describeHomeworkOpenBadge(input: HomeworkOpenBadgeInput): string {
  const planDate = input.planDate;
  const deadline = (input.deadlineDate ?? '').trim() || planDate;

  const planLabel = planDateLabel(planDate);
  return `📅 ${planLabel} 布置 · ${deadlineLabel(deadline, planDate, input.today)}`;
}

/** `'周五 10-16'`（跨年时退化成 `'周五 2027-01-01'`，与 `homeworkDueDateLabel` 同口径）。 */
function planDateLabel(planDate: string): string {
  const weekday = WEEKDAY_LABELS[weekdayOf(planDate)];
  if (!weekday) return planDate;
  return `${weekday} ${planDate.slice(5)}`;
}

/**
 * `'周日 10-18 截止'` / `'今天截止'` / `'明天截止'` / `'当天截止'`
 * —— **永不含时刻**。
 *
 * ⚠️ 中文与"截止"之间**不加空格**（`今天截止`，不是 `今天 截止`），
 * 只有"周日 10-18"这种"星期 + 日期"内部才用空格分隔。
 */
function deadlineLabel(deadline: string, planDate: string, today: string): string {
  if (deadline <= planDate) return '当天截止';
  if (deadline === today) return '今天截止';
  if (deadline === addDays(today, 1)) return '明天截止';

  const weekday = WEEKDAY_LABELS[weekdayOf(deadline)];
  if (!weekday) return `${deadline} 截止`;
  // 跨年时补上年份：`01-02` 单独出现看不出是哪一年（与 homeworkDueDateLabel 同一理由）
  const sameYear = deadline.slice(0, 4) === today.slice(0, 4);
  return `${weekday} ${sameYear ? deadline.slice(5) : deadline} 截止`;
}

/**
 * 「还有效」区块的**说明文字**（学生端/家长端共用，逐字）。
 *
 * 学生端那句刻意**不带催促**（与 `describeStudentHomeworkDueText()` 同一口径：
 * 不写"还剩""尽快""别拖"）：他知道期限就够了。
 */
export function describeHomeworkOpenQueueText(parentView: boolean): string {
  return parentView
    ? '这些是还没到期的作业（不是今天布置的，但也还没超时）—— 孩子那边现在就能做，' +
        '做完点「完成」交给你验收；一直不做的话，就点「放弃」把它收掉。'
    : '这些作业还没到期，现在就能做 —— 做完点「完成」交给家长验收，它才会从这里消失。';
}
